import { BRAND_REVIEW_STATE_LABEL, brandReviewState, briefingLines, emptyBriefing, type BriefingFields, type BriefingRow } from "@talentkontor/domain";
import type { Pill } from "../../../model/invoice-view";
import { pill } from "./model";
import type { BrandContactLite, BrandReviewEventRow, BrandReviewRow, BriefingEingabe } from "./marke-daten";
import { t, ta } from "../../../lib/sprache";

/**
 * Anzeige zur Marke im Agentur-Bereich (0073): Kontaktvorschlag für die Freigabe, Stand eines Links als Pille, Beweiskette als
 * lesbare Zeilen, Briefing-Formular. Reine Funktionen, getestet in apps/web/test/marke-agentur.test.ts.
 */

/**
 * Freigabe-Kontakt: Rolle „Freigabe“, sonst „Briefing“, sonst der erste mit Adresse; bei gleicher Rolle die Marke selbst vor
 * der Agentur, die sie vertritt (wie app.brand_approval_contact, dazu der Rückfall). Die Kontakte kommen nach Anlage sortiert.
 */
export function vorschlagKontakt(kontakte: readonly BrandContactLite[], eigeneMarke?: string | null): BrandContactLite | null {
  const mitMail = kontakte.filter((k) => k.email?.trim());
  const rang = (k: BrandContactLite) => (k.roles.includes("approval") ? 0 : k.roles.includes("briefing") ? 2 : 4) + (eigeneMarke && k.brand_id !== eigeneMarke ? 1 : 0);
  return [...mitMail].sort((a, b) => rang(a) - rang(b))[0] ?? null;
}

export function freigabePill(r: Pick<BrandReviewRow, "status" | "expires_at">, now: Date = new Date()): Pill {
  const s = brandReviewState(r, now);
  const l = BRAND_REVIEW_STATE_LABEL[s];
  return pill(l.de, l.tone);
}

/** Offene Links je Beitrag (bzw. je Einreichung); ein abgelaufener offener Link zählt nicht als „bei der Marke“. */
export function beiDerMarke<T extends Pick<BrandReviewRow, "status" | "expires_at" | "deliverable_id" | "submission_id">>(reviews: readonly T[], now: Date = new Date()): {
  jeBeitrag: Map<string, T>; jeEinreichung: Map<string, T>;
} {
  const jeBeitrag = new Map<string, T>(), jeEinreichung = new Map<string, T>();
  for (const r of reviews) {
    if (brandReviewState(r, now) !== "open") continue;
    jeEinreichung.set(r.submission_id, r);
    if (r.deliverable_id) jeBeitrag.set(r.deliverable_id, r);
  }
  return { jeBeitrag, jeEinreichung };
}

const EREIGNIS: Record<string, string> = {
  get created() { return t("Link erzeugt", "Link generated"); }, get sent() { return t("Per Mail an die Marke", "By email to the brand"); }, get opened() { return t("Von der Marke geöffnet", "Opened by the brand"); }, file: "Datei abgerufen", get approved() { return t("Freigegeben", "Approved"); },
  get changes_requested() { return t("Änderung gewünscht", "Change requested"); }, get withdrawn() { return t("Zurückgezogen", "Withdrawn"); }, get closed() { return t("Geschlossen", "Closed"); }, get expired() { return t("Abgelaufen", "Expired"); }, get forwarded() { return t("An den Creator weitergegeben", "Passed on to the creator"); },
};

const GRUND: Record<string, string> = {
  get replaced() { return t("durch neuen Link ersetzt", "replaced by a new link"); }, get team() { return ta("vom Team entschieden", "decided by the team", "von dir entschieden", "decided by you"); }, get cancelled() { return t("Beitrag entfallen", "Post dropped"); }, get deal_cancelled() { return t("Kooperation storniert", "Deal cancelled"); },
  get period_cancelled() { return t("Monat storniert", "Month cancelled"); },
};

/** Beweiskette: „Von der Marke geöffnet · 203.0.113.7 · Firefox …“ */
export function ereignisZeile(e: Pick<BrandReviewEventRow, "event" | "ip" | "user_agent" | "details">): string {
  const d = e.details ?? {};
  const extra = [
    typeof d.name === "string" ? d.name : null,
    typeof d.recipient === "string" ? d.recipient : null,
    typeof d.round === "number"
      ? t(`Korrekturschleife ${d.round}${d.extra_round === true ? d.extra_acknowledged === true ? t(" (zusätzlich, von der Marke bestätigt)", " (additional, confirmed by the brand)") : t(" (zusätzlich, ohne Bestätigung der Marke)", " (additional, without the brand's confirmation)") : ""}`, `Revision round ${d.round}${d.extra_round === true ? d.extra_acknowledged === true ? t(" (zusätzlich, von der Marke bestätigt)", " (additional, confirmed by the brand)") : t(" (zusätzlich, ohne Bestätigung der Marke)", " (additional, without the brand's confirmation)") : ""}`)
      : null,
    typeof d.reason === "string" ? GRUND[d.reason] ?? d.reason : null,
    e.ip ? e.ip.replace(/\/32$/, "") : null,
    e.user_agent ? e.user_agent.slice(0, 60) : null,
  ].filter(Boolean);
  return [EREIGNIS[e.event] ?? e.event, ...extra].join(" · ");
}

export function leereBriefingEingabe(): BriefingEingabe {
  return { key_messages: [], dos: [], donts: [], hashtags: [], mentions: [], ad_label: "", paid_partnership: false, deadline: "", notes: "" };
}

export function eingabeAus(b: BriefingFields | null | undefined): BriefingEingabe {
  const x = b ?? emptyBriefing();
  return {
    key_messages: [...x.key_messages], dos: [...x.dos], donts: [...x.donts], hashtags: [...x.hashtags], mentions: [...x.mentions],
    ad_label: x.ad_label ?? "", paid_partnership: x.paid_partnership, deadline: x.deadline ?? "", notes: x.notes ?? "",
  };
}

/** Textfelder des Formulars (eine Zeile je Eintrag; Hashtags und Markierungen mit Leerzeichen getrennt) in die Eingabe. */
export function eingabeAusText(t: { botschaften: string; dos: string; donts: string; hashtags: string; markierungen: string; kennzeichnung: string; partnerschaft: boolean; deadline: string; hinweise: string }): BriefingEingabe {
  return {
    key_messages: briefingLines(t.botschaften, "text"), dos: briefingLines(t.dos, "text"), donts: briefingLines(t.donts, "text"),
    hashtags: briefingLines(t.hashtags, "hashtag"), mentions: briefingLines(t.markierungen, "mention"),
    ad_label: t.kennzeichnung, paid_partnership: t.partnerschaft, deadline: t.deadline, notes: t.hinweise,
  };
}

export function textAusEingabe(e: BriefingEingabe) {
  return {
    botschaften: e.key_messages.join("\n"), dos: e.dos.join("\n"), donts: e.donts.join("\n"), hashtags: e.hashtags.map((h) => `#${h}`).join(" "),
    markierungen: e.mentions.map((m) => `@${m}`).join(" "), kennzeichnung: e.ad_label, partnerschaft: e.paid_partnership, deadline: e.deadline, hinweise: e.notes,
  };
}

/** Briefing der Kooperation (ohne Beitrag) und je Beitrag aus den Zeilen. */
export function briefingsNachEbene(rows: readonly BriefingRow[]): { deal: BriefingRow | null; jeBeitrag: Map<string, BriefingRow> } {
  return { deal: rows.find((r) => !r.deliverable_id) ?? null, jeBeitrag: new Map(rows.filter((r) => r.deliverable_id).map((r) => [r.deliverable_id!, r])) };
}
